import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { it as itLocale, es as esLocale } from "date-fns/locale";
import { CalendarDays, Clock, Send, MessageCircle } from "lucide-react";
import Reveal from "../landing/Reveal";
import { waLink } from "./FloatingWhatsApp";
import { useCalc } from "@/state/CalcContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIME_SLOTS = ["09:00", "10:00", "11:00", "12:00", "15:00", "16:00", "17:00", "18:00"];

const initialForm = { name: "", organization: "", email: "", role: "", message: "" };

export default function Contacto() {
  const { t, lang } = useLanguage();
  const { calc } = useCalc();
  const dateLocale = lang === "es" ? esLocale : itLocale;
  const [tab, setTab] = useState("demo");
  const [form, setForm] = useState(initialForm);
  const [date, setDate] = useState(undefined);
  const [time, setTime] = useState("");
  const [sending, setSending] = useState(false);

  const update = (field) => (event) => setForm({ ...form, [field]: event.target.value });

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (tab === "call" && (!date || !time)) {
      toast.error(t.contact.toasts.needDateTime);
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/demo-requests`, {
        ...form,
        request_type: tab,
        preferred_date: tab === "call" && date ? format(date, "yyyy-MM-dd") : null,
        preferred_time: tab === "call" ? time : null,
        ...(calc
          ? {
              calc_population: calc.population,
              calc_interventions: calc.interventions,
              calc_reactive_low: calc.reactiveLow,
              calc_reactive_high: calc.reactiveHigh,
              calc_savings: calc.savings,
              calc_km: calc.km,
              calc_co2: calc.co2,
            }
          : {}),
      });
      toast.success(tab === "call" ? t.contact.toasts.callOk : t.contact.toasts.demoOk);
      setForm(initialForm);
      setDate(undefined);
      setTime("");
    } catch (error) {
      toast.error(t.contact.toasts.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contacto" className="circuit-bg bg-forest-900 py-20 md:py-28" data-testid="contacto-section">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <Reveal className="mx-auto max-w-4xl text-center">
          <p className="kicker" data-testid="contacto-kicker">{t.contacto.kicker}</p>
          <h2 className="mt-5 font-display text-3xl font-bold leading-tight text-cream md:text-4xl lg:text-5xl" data-testid="contacto-title">
            {t.contacto.title}
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-sm leading-relaxed text-cream/70 md:text-base">
            {t.contacto.sub}
          </p>
          <div className="rule-light mx-auto mt-10 max-w-md pt-8">
            <p className="font-body text-[11px] font-bold uppercase tracking-[0.3em] text-cream/50">
              {t.contacto.siteLabel}
            </p>
            <p className="mt-2 font-display text-3xl font-bold italic text-mint" data-testid="contacto-site">
              {t.contacto.site}
            </p>
            <a
              href={`mailto:${t.contacto.email}`}
              data-testid="contacto-email"
              className="mt-3 inline-block font-mono text-sm text-cream/70 underline-offset-4 transition-colors hover:text-mint hover:underline"
            >
              {t.contacto.email}
            </a>
            <span className="mx-3 text-cream/25">·</span>
            <a
              href={`tel:${t.contacto.phone.replace(/\s/g, "")}`}
              data-testid="contacto-phone"
              className="mt-3 inline-block font-mono text-sm text-cream/70 underline-offset-4 transition-colors hover:text-mint hover:underline"
            >
              {t.contacto.phone}
            </a>
            <div className="mt-5">
              <a
                href={waLink(t.contacto.waMessage)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contacto-whatsapp"
                className="inline-flex items-center gap-2 border border-[#25D366]/60 px-6 py-3 text-sm font-bold text-[#4ce081] transition-colors hover:bg-[#25D366] hover:text-forest-950"
              >
                <MessageCircle size={17} /> {t.contacto.whatsapp}
              </a>
            </div>
            <p className="mt-6 text-xs leading-relaxed text-cream/45" data-testid="contacto-address">
              {t.contacto.address}
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="mx-auto mt-14 max-w-2xl">
          <div className="bg-cream p-7 text-forest-800 shadow-2xl shadow-black/30 md:p-10" data-testid="contact-form-card">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="grid w-full grid-cols-2" data-testid="contact-tabs">
                <TabsTrigger value="demo" data-testid="tab-demo">{t.contact.tabDemo}</TabsTrigger>
                <TabsTrigger value="call" data-testid="tab-call">{t.contact.tabCall}</TabsTrigger>
              </TabsList>

              <form onSubmit={handleSubmit} className="mt-7 space-y-5" data-testid="contact-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="contact-name">{t.contact.labels.name}</Label>
                    <Input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={update("name")} placeholder={t.contact.placeholders.name} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-organization">{t.contact.labels.organization}</Label>
                    <Input id="contact-organization" data-testid="contact-organization-input" required value={form.organization} onChange={update("organization")} placeholder={t.contact.placeholders.organization} />
                  </div>
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="contact-email">{t.contact.labels.email}</Label>
                    <Input id="contact-email" type="email" data-testid="contact-email-input" required value={form.email} onChange={update("email")} placeholder={t.contact.placeholders.email} />
                  </div>
                  <div className="space-y-2">
                    <Label>{t.contact.labels.role}</Label>
                    <Select value={form.role} onValueChange={(value) => setForm({ ...form, role: value })}>
                      <SelectTrigger data-testid="contact-role-select">
                        <SelectValue placeholder={t.contact.placeholders.role} />
                      </SelectTrigger>
                      <SelectContent>
                        {t.contact.roles.map((role) => (
                          <SelectItem key={role} value={role} data-testid={`role-option-${t.contact.roles.indexOf(role)}`}>{role}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <TabsContent value="call" className="mt-0 space-y-4" data-testid="call-booking-panel">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2"><CalendarDays size={14} /> {t.contact.labels.day}</Label>
                      <div className="border border-forest-800/25 bg-white p-2">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          locale={dateLocale}
                          disabled={{ before: new Date() }}
                          data-testid="call-date-calendar"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2"><Clock size={14} /> {t.contact.labels.time}</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {TIME_SLOTS.map((slot) => (
                          <button
                            key={slot}
                            type="button"
                            data-testid={`time-slot-${slot.replace(":", "")}`}
                            onClick={() => setTime(slot)}
                            className={`border px-3 py-2 font-mono text-sm transition-colors ${
                              time === slot
                                ? "border-ember bg-ember/10 font-bold text-ember"
                                : "border-forest-800/25 text-forest-800/80 hover:border-ember/60"
                            }`}
                          >
                            {slot}
                          </button>
                        ))}
                      </div>
                      {date && time && (
                        <p className="pt-1 font-mono text-xs font-bold text-ember" data-testid="call-summary">
                          {format(date, "EEEE d MMMM", { locale: dateLocale })} · {time}
                        </p>
                      )}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="demo" className="mt-0" data-testid="demo-panel">
                  <div className="space-y-2">
                    <Label htmlFor="contact-message">{t.contact.labels.message}</Label>
                    <Textarea id="contact-message" data-testid="contact-message-input" rows={4} value={form.message} onChange={update("message")} placeholder={t.contact.placeholders.message} />
                  </div>
                </TabsContent>

                <Button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-btn"
                  className="w-full bg-forest-800 text-sm font-bold text-cream hover:bg-forest-700"
                >
                  <Send size={15} className="mr-2" />
                  {sending ? t.contact.sending : tab === "call" ? t.contact.submitCall : t.contact.submitDemo}
                </Button>
              </form>
            </Tabs>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
